import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { pathOr } from 'remeda';
import HTML from 'shared/components/HTML/HTML';
import { type ThirdParties, ThirdPartyType } from 'shared/hooks/use-load-third-parties';
import { Button, CustomSelect } from 'shared/io';
import type { onSubmitFunction } from 'types/react-hook-form';
import type { StoreState } from 'types/storeTypes';

import styleIdentifiers from './selectThirdPartyForm.module.scss';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  onSubmit: onSubmitFunction;
  onSubmitGcollect?: onSubmitFunction;
  onSubmitLegalCity?: onSubmitFunction;
  onSubmitAML?: onSubmitFunction;
  selectedThirdPartyType: ThirdPartyType;
  thirdParties: ThirdParties;
};

export default function SelectThirdPartyForm({
  onSubmit,
  onSubmitGcollect,
  onSubmitLegalCity,
  onSubmitAML,
  selectedThirdPartyType,
  thirdParties,
}: Props) {
  const { t } = useTranslation();
  const isLoading = useSelector((state: StoreState) => state.invoice.isLoading);
  const form = useForm({ shouldUnregister: true });
  const { watch } = form;

  const { availableCompanies } = thirdParties[selectedThirdPartyType];
  const selectedThirdPartyCompanyId = watch('third_party_company_id');
  const selectedThirdPartyCompany = availableCompanies.find(
    company => company.id === selectedThirdPartyCompanyId,
  );
  // @ts-expect-error bad type inference on the isGcollect property
  const isGcollect = pathOr(selectedThirdPartyCompany, ['isGcollect'], false);
  // @ts-expect-error bad type inference on the isLegalCity property
  const isLegalCity = pathOr(selectedThirdPartyCompany, ['isLegalCity'], false);
  // @ts-expect-error bad type inference on the shouldShowAmlForm property
  const shouldShowAmlForm = pathOr(selectedThirdPartyCompany, ['shouldShowAmlForm'], false);

  const handleSubmit = () => {
    const submitFunction = (() => {
      if (isGcollect && onSubmitGcollect) return onSubmitGcollect;
      if (isLegalCity && onSubmitLegalCity) return onSubmitLegalCity;
      if (shouldShowAmlForm && onSubmitAML) return onSubmitAML;
      return onSubmit;
    })();
    submitFunction(form.getValues(), onSubmit);
  };

  return (
    <FormProvider {...form}>
      <div className={styles('select-third-party-form')}>
        <div className={styles('wrapper')}>
          {selectedThirdPartyType === ThirdPartyType.BAILIFF && (
            <div className={styles('description')}>
              <HTML html={t(i18nKeys.FORM.CONFIRM_BAILIFF.DESCRIPTION)} />
            </div>
          )}
          <div className={styles('content')}>
            <Controller
              defaultValue=""
              rules={{ required: true }}
              name="third_party_company_id"
              render={() => (
                <CustomSelect
                  items={thirdParties[selectedThirdPartyType].availableCompanies}
                  name="third_party_company_id"
                  placeholder={t(i18nKeys.SELECT)}
                  keyText="name"
                  keyValue="id"
                  className={styles('input')}
                />
              )}
            />
          </div>
          <div className={styles('button-wrapper')}>
            <Button
              isLoading={isLoading}
              label={t(i18nKeys.CONFIRM)}
              onClick={handleSubmit}
              className={styles('button')}
            />
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
